import { useEffect, useRef } from 'react';
import { api } from '@/services/api';
import {
  applicationsToBulkStatusRefs,
  applyBulkStatusPatches,
  BULK_STATUS_API_MAX_PER_REQUEST,
  shouldClearProcessingIndicator,
} from '@/lib/bulkProcessQueueSync';
import { useApplicationListSync } from '@/hooks/useApplicationListSync';
import {
  isApplicationPipelineRecentlyInFlight,
} from '@/lib/pendingProcessing';
import type { Application } from '@/types';

const PIPELINE_POLL_MS = 5_000;

function isPipelineStatusPollCandidate(app: Application): boolean {
  if (app.archived === true) return false;
  if (shouldClearProcessingIndicator(app.state)) return false;
  if (app.state === 'PIPELINE_RUNNING') return true;
  return isApplicationPipelineRecentlyInFlight(app);
}

/**
 * Polls BE for in-flight automatic screenings and patches list caches when state settles
 * (e.g. PIPELINE_RUNNING → AWAITING_DECISION) without opening application detail.
 */
export function usePipelineStatusPoll(
  companyId: string | undefined,
  applications: Application[],
  enabled: boolean,
): void {
  const listSync = useApplicationListSync();
  const applicationsRef = useRef(applications);
  applicationsRef.current = applications;

  useEffect(() => {
    if (!enabled || !companyId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      if (cancelled) return;
      const apps = applicationsRef.current;
      const pending = apps.filter(isPipelineStatusPollCandidate);
      if (pending.length === 0) return;

      const refs = applicationsToBulkStatusRefs(pending);
      if (refs.length === 0) return;

      try {
        for (let i = 0; i < refs.length; i += BULK_STATUS_API_MAX_PER_REQUEST) {
          const chunk = refs.slice(i, i + BULK_STATUS_API_MAX_PER_REQUEST);
          const items = await api.applications.bulkProcessingStatus(companyId, chunk);
          if (cancelled || items.length === 0) continue;
          const patched = applyBulkStatusPatches(apps, items);
          const changed = patched.filter((row) => {
            const prev = apps.find((a) => a.id === row.id);
            return prev && (prev.state !== row.state || prev.aiScore !== row.aiScore);
          });
          if (changed.length > 0) {
            listSync.patchMany(companyId, changed, false);
          }
        }
      } catch {
        // Transient poll errors — retry on next interval.
      }
    };

    const schedule = () => {
      timer = setTimeout(() => {
        void tick().finally(() => {
          if (!cancelled) schedule();
        });
      }, PIPELINE_POLL_MS);
    };

    void tick();
    schedule();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [companyId, enabled, listSync]);
}
